import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import PageShell from "@/components/layout/PageShell";
import api from "@/lib/api";
import CaseloadProgress from "./progress/CaseloadProgress";
import StudentProgress from "./progress/StudentProgress";
import GoalProgress from "./progress/GoalProgress";

export default function Progress() {
  const [params, setParams] = useSearchParams();
  const studentId = params.get("student");
  const goalId = params.get("goal");
  // FP3-035F correction (Issue 2) — Progress uses `setParams` to
  // navigate between Caseload → Student → Goal views inside a
  // single route. The raw `setParams({ student: id })` replaces
  // the entire query string, which silently dropped `?as_provider=`
  // and booted admin back into their own context the moment they
  // clicked a student from the read-only Progress list. Helper
  // below merges the new params on top of whatever was already on
  // the URL so `as_provider` (and any other passthrough) survives.
  const asProvider = params.get("as_provider") || "";
  const mergeParams = (next) => {
    const merged = { ...next };
    if (asProvider) merged.as_provider = asProvider;
    setParams(merged);
  };
  const [caseload, setCaseload] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .getCaseloadProgress({})
      .then((r) => setCaseload(r.data))
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
  }, []);

  const handleSelectStudent = (id) => {
    mergeParams({ student: id });
  };

  const handleSelectGoal = (id) => {
    mergeParams({ student: studentId, goal: id });
  };

  const handleBackToCaseload = () => {
    mergeParams({});
  };

  const handleBackToStudent = () => {
    if (studentId) mergeParams({ student: studentId });
    else mergeParams({});
  };

  return (
    <PageShell testId="progress-page" variant="compact">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-[#1F2937] heading-font tracking-tight" data-testid="progress-title">
          Progress
        </h1>
        <p className="text-sm text-[#6B7280] mt-1">
          {goalId
            ? "Goal trend and trial-run history"
            : studentId
            ? "Student goal progress over time"
            : "Caseload-wide progress and attention flags"}
        </p>
      </div>

      {goalId ? (
        <GoalProgress goalId={goalId} onBack={handleBackToStudent} />
      ) : studentId ? (
        <StudentProgress
          studentId={studentId}
          onBack={handleBackToCaseload}
          onSelectGoal={handleSelectGoal}
        />
      ) : loading ? (
        <div className="space-y-3" data-testid="progress-loading">
          <div className="grid grid-cols-6 gap-2 mb-5">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-20" />
            ))}
          </div>
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : (
        <CaseloadProgress data={caseload} onSelectStudent={handleSelectStudent} />
      )}
    </PageShell>
  );
}
